import React, { useMemo, useState, MouseEvent } from 'react'
import { useSelector } from 'react-redux'

import { Button } from '../../../common/components/Button'
import AddIcon from '../../../lib/icons/add-icon/add-icon'
import { Table, TableBody, TableBodyCell, TableHeader, TableHeaderCell, TableRow } from '../../../lib/table'
import { RootState } from '../../redux/store'
import localizeDate from '../../../common/utils/localizeDate'
import { CheckMarkIcon } from '../../../common/icons/checkmark-icon'
import AlertModal from '../../../ui/AlertModal'
import { notifySuccess, notifyError } from '../../../common/features/notificationsManager/Notify'
import {
    useReportGetReportsForAdminQuery,
    useCompanyGetReportDeliverySchedulesQuery,
    useCompanyDeleteReportDeliveryScheduleMutation,
    ReportDeliverySchedule,
} from '../../../common/redux/api/web-api'
import { WidgetLayout } from '../../../lib/widget-layout'
import Text from '../../../ui/Text'
import { AddReportDeliveryModal } from '../add-report-delivery-modal/AddReportDeliveryModal'
import DropdownMenu, { DropdownMenuItem } from '../../../common/components/DropdownMenu'
import TrashIcon from '../../../lib/icons/trash-icon/trash-icon'
import useText from '../../../common/hooks/useText'

export const AutomaticReportDeliveriesTable = () => {
    const text = useText()
    const { selectedCompanyId } = useSelector((state: RootState) => state.user)

    const [addReportDeliveryModal, setAddReportDeliveryModal] = useState<{
        isOpen: boolean
        reportDeliverySchedule?: ReportDeliverySchedule
    }>({ isOpen: false })
    const [confirmDeleteReportModal, setIsConfirmDeleteReportModalOpen] = useState<{
        isOpen: boolean
        reportId?: number
        name?: string
    }>({ isOpen: false })

    const { data: reports, isLoading } = useReportGetReportsForAdminQuery({
        selectedCompanyId,
    })
    const reportNames = useMemo(() => new Map(reports?.map((i) => [i.id, i.name])), [reports])

    const { data: reportDeliverySchedules } = useCompanyGetReportDeliverySchedulesQuery({
        companyId: selectedCompanyId,
    })

    const [deleteDeliveryScheduleRequest] = useCompanyDeleteReportDeliveryScheduleMutation()
    const deleteDeliverySchedule = async (reportDeliveryScheduleId: number) => {
        try {
            await deleteDeliveryScheduleRequest({ companyId: selectedCompanyId, reportDeliveryScheduleId }).unwrap()
            notifySuccess('Report delivery schedule has been removed')
        } catch (_) {
            notifyError('Failed to remove report delivery schedule')
        }
    }
    return (
        <>
            <WidgetLayout
                title={text('lblAutomaticReportDeliveriesField')}
                headerButtons={
                    <Button icon={<AddIcon />} size="sm" onClick={() => setAddReportDeliveryModal({ isOpen: true })}>
                        {text('lblAdd')}
                    </Button>
                }
            >
                <Table isLoading={isLoading}>
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell>{text('lblActiveStatus')}</TableHeaderCell>
                            <TableHeaderCell>{text('lblReport')}</TableHeaderCell>
                            <TableHeaderCell>{text('lblType')}</TableHeaderCell>
                            <TableHeaderCell>{text('lblInterval')}</TableHeaderCell>
                            <TableHeaderCell>{text('lblReceiverEmail')}</TableHeaderCell>
                            <TableHeaderCell>{text('lblLastSent')}</TableHeaderCell>
                            <TableHeaderCell></TableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody isLoading={isLoading} numberOfLoadingColumns={6}>
                        {reportDeliverySchedules?.map((nextReportDeliverySchedule: ReportDeliverySchedule) => (
                            <TableRow
                                key={nextReportDeliverySchedule.id}
                                className="pointer"
                                onClick={() =>
                                    setAddReportDeliveryModal({
                                        isOpen: true,
                                        reportDeliverySchedule: nextReportDeliverySchedule,
                                    })
                                }
                            >
                                <TableBodyCell className="w-1rem">
                                    {nextReportDeliverySchedule.active && (
                                        <div className="flex justify-content-center">
                                            <CheckMarkIcon size={18} />
                                        </div>
                                    )}
                                </TableBodyCell>
                                <TableBodyCell>
                                    {nextReportDeliverySchedule.reportId &&
                                        reportNames.get(nextReportDeliverySchedule.reportId)}
                                </TableBodyCell>
                                <TableBodyCell>{nextReportDeliverySchedule.deliveryType}</TableBodyCell>
                                <TableBodyCell>{nextReportDeliverySchedule.deliveryInterval}</TableBodyCell>
                                <TableBodyCell>{nextReportDeliverySchedule.receiverEmail}</TableBodyCell>
                                <TableBodyCell>{localizeDate(nextReportDeliverySchedule.lastSent)}</TableBodyCell>
                                <TableBodyCell>
                                    <DropdownMenu
                                        id={nextReportDeliverySchedule.reportId}
                                        data-testid="menu-toggle-report"
                                    >
                                        <DropdownMenuItem
                                            onClick={(event: MouseEvent<HTMLLIElement>) => {
                                                event.stopPropagation()
                                                setIsConfirmDeleteReportModalOpen({
                                                    isOpen: true,
                                                    reportId: nextReportDeliverySchedule.id,
                                                    name: reportNames.get(nextReportDeliverySchedule.reportId),
                                                })
                                            }}
                                            text={text('btnRemove')}
                                            icon={<TrashIcon size={14} />}
                                            key="btnRemoveReport"
                                            data-testid="btn-remove-report"
                                        />
                                    </DropdownMenu>
                                </TableBodyCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </WidgetLayout>

            <AddReportDeliveryModal
                visible={addReportDeliveryModal.isOpen}
                onHide={() => setAddReportDeliveryModal({ isOpen: false, reportDeliverySchedule: undefined })}
                selectedReportDeliverySchedule={addReportDeliveryModal.reportDeliverySchedule}
            />

            {confirmDeleteReportModal.isOpen && (
                <AlertModal
                    title={text('lblRemoveReport')}
                    message={
                        <Text
                            code="msgConfirmReportRemoving"
                            values={{
                                reportName: confirmDeleteReportModal.name,
                            }}
                        />
                    }
                    showCancel={true}
                    dismiss={() => setIsConfirmDeleteReportModalOpen({ isOpen: false })}
                    close={(result) => {
                        if (result && confirmDeleteReportModal.reportId) {
                            deleteDeliverySchedule(confirmDeleteReportModal.reportId)
                        }
                        setIsConfirmDeleteReportModalOpen({ isOpen: false })
                    }}
                />
            )}
        </>
    )
}
